<script lang="ts" module>
  import DataTableLayout from './Layout.svelte';
  import { mostSevereAlert } from '#lib/zod/output/TabularData.js';
  import type {
    Column,
    Columns,
    FlatColumns,
    TableRow,
    TableRowLazyGroup,
  } from '#lib/zod/output/TabularData.js';
  import DataTableRow from './Row.svelte';
  import {
    aggregateRows,
    colGroupBounds,
    firstOrdinaryRowIndex,
    traverseTable,
    convertTableRowUnits,
    tableToCsvStream,
    type TableDownloadOptions,
  } from './utils';
  import { createTableState, type FlatRow, type CellAccessor } from './state.svelte';
  import { createVirtualizer } from './virtualizer.svelte';
  import Checkbox from '../Checkbox.svelte';
  import type { DeepReadonly } from '#lib/utils/type.js';
  import FilterRow from '../filters/FilterRow.svelte';
  import DataTableHeader from './Header.svelte';
  import Tooltip from '../Tooltip.svelte';
  import { writable } from 'svelte/store';
  import { filter, map } from '#lib/utils/generators.js';
  import DataTableSortingIcon from './SortingIcon.svelte';
  import type { Unit } from '#lib/overview/utils/units.js';
  import { getScrollParent } from '#lib/utils/scrollParent.js';
  import dayjs from 'dayjs';
  import { downloadIterable } from '#lib/utils/download.js';
  import { SvelteSet } from 'svelte/reactivity';
  import { onMount, untrack, type Snippet } from 'svelte';
  import { setContext } from '#lib/utils/svelte-context.js';
  import {
    DataTableContextKey,
    type CountHintType,
    type RowNameFunction,
    type TooltipStore,
  } from './types';
  import type { ClassValue } from 'svelte/elements';
</script>

<script lang="ts">
  interface Props {
    rows: TableRow[];
    lazyRowExpand?: (r: TableRowLazyGroup) => Promise<TableRow[]>;
    nameColTitle?: string;
    nameFunction?: RowNameFunction;
    countHint?: CountHintType;
    countHintTooltip?: string | { level: number; text: string | undefined }[];
    cols: DeepReadonly<Columns>;
    useAnyTagFilter?: boolean;
    tagLabel?: (tagTitle: string) => string;
    tagTitle?: (tagTitle: string) => string;
    hidePeak?: boolean;
    showSelectedAggregate?: boolean;
    selectableRows?: boolean;
    showGroupAggregate?: boolean;
    autoselectAllRows?: boolean;
    filterLeaves?: boolean;
    /** How many layers of TableRowGroup from the top should be name only */
    nameOnlyLayers?: number;
    quickSelect?: Snippet<[]>;
    showAlertColumn?: boolean;
    expanded?: boolean | number;
    onRowClick?: (r: TableRow) => void;
    onSelectedChange?: (rows: TableRow[]) => void;
    rowHref?: (r: TableRow, ancestors: TableRow[]) => string | undefined;
    /** If defined, will try to set units at specific levels of the table */
    unitConversion?: Record<number, Unit>;
    rowNameBold?: boolean;
    showGroupHeaderAlerts?: boolean;
    topRightChild?: Snippet<[]>;
    /**
     * Explicit pixel widths for each column type.
     * Body rows are laid out as CSS grid, so widths must be set explicitly.
     * Defaults: checkbox=40, alert=32, name=250, data=120.
     */
    columnWidths?: {
      checkbox?: number;
      alert?: number;
      name?: number | string;
      data?: number | string;
    };

    wrapperClass?: ClassValue;
  }

  let {
    rows,
    lazyRowExpand,
    nameColTitle = 'System',
    nameFunction,
    countHint,
    countHintTooltip,
    cols,
    useAnyTagFilter = false,
    tagLabel = (_) => _,
    tagTitle = (_) => _,
    hidePeak = false,
    showSelectedAggregate = false,
    selectableRows = false,
    showGroupAggregate = true,
    autoselectAllRows = false,
    nameOnlyLayers = 0,
    quickSelect,
    wrapperClass,
    showAlertColumn = false,
    expanded = true,
    filterLeaves = false,
    onRowClick,
    onSelectedChange,
    rowHref,
    unitConversion,
    rowNameBold = false,
    showGroupHeaderAlerts = false,
    topRightChild,
    columnWidths,
  }: Props = $props();

  // ## Table
  let flatCols = $derived(
    <FlatColumns>(
      cols.flatMap((g) => ('columns' in g ? g.columns.map((c) => ({ ...c, group: g })) : g))
    ),
  );

  // Cell accessor: extracts a cell value from a row for a given column index
  const cellAccessor: CellAccessor = (row, columnIndex) => {
    if (columnIndex === -1) return undefined; // Name column handled separately
    const c = flatCols[columnIndex];
    if (!c) return undefined;

    if (!c.type || c.type === 'data') {
      return row.data?.[c.key];
    }

    if (c.type === 'date-like-parameter') {
      return dayjs(row.parameters[c.parameter] as any);
    }

    return undefined;
  };

  let filterFn = $state<(r: TableRow) => boolean>(() => true);

  const tableState = createTableState({
    rows: () => rows,
    columns: () => flatCols,
    filterFn: () => filterFn,
    filterLeaves: () => filterLeaves,
    hidePeak: () => hidePeak,
    cellAccessor,
  });

  export const downloadCsv = (filename: string, options: TableDownloadOptions) => {
    downloadIterable(tableToCsvStream(tableState.getAllRowsFlat(), flatCols, options), filename);
  };

  $effect(() => {
    onSelectedChange?.(tableState.getSelectedRows());
  });

  $effect(() => {
    if (rows && autoselectAllRows) {
      untrack(() => tableState.selectAll(true));
    }
  });

  // #####
  // ## Sorting
  // #####

  const columnClicked = (col: Column | 'name' | 'count') => {
    if (col === 'count') return;
    if (col === 'name') return tableState.toggleSortByName();

    tableState.toggleSort(flatCols.indexOf(col));
  };

  // ########
  // ## Expanding
  // ########
  const loadingRowsIDs = new SvelteSet<string>();

  function reExpand() {
    loadingRowsIDs.clear();
    if (!expanded) {
      tableState.expandAll(false);
      return;
    }
    if (typeof expanded === 'boolean') {
      tableState.expandAll();
    } else {
      tableState.expandAll(expanded);
    }
  }

  const expandRow = async (flatRow: FlatRow, s: boolean) => {
    if (
      flatRow.original.type === 'lazyGroup' &&
      flatRow.original.children === undefined &&
      lazyRowExpand
    ) {
      loadingRowsIDs.add(flatRow.id);

      const kids = await lazyRowExpand(flatRow.original);
      flatRow.original.children = kids;

      tableState.refreshRows();
      loadingRowsIDs.delete(flatRow.id);
    }

    tableState.toggleExpand(flatRow.id, s);
  };

  const tooltip: TooltipStore = writable(undefined);

  let tableWrapper = $state<HTMLDivElement>();
  const scrollParent = $derived(tableWrapper ? getScrollParent(tableWrapper) : undefined);

  let showCheckboxes = $derived(!selectableRows ? showSelectedAggregate : selectableRows);
  let drawColumnGroups = $derived(cols.some((c) => 'columns' in c));

  // Thresholds
  let rowAlerts = $derived(
    new Map<TableRow, 1 | 2 | 3>(
      filter(
        map(traverseTable(rows), (r) => [r, mostSevereAlert(flatCols, r)] as const),
        (x) => typeof x[1] === 'number' && x[1] > 0,
      ) as Generator<[TableRow, 1 | 2 | 3]>,
    ),
  );
  let hasAlerts = $derived(showAlertColumn && rowAlerts.size > 0);
  let firstColspan = $derived(firstOrdinaryRowIndex(true, showCheckboxes, hasAlerts));
  let borders = $derived(
    new Set(
      colGroupBounds(cols)
        .flatMap((c) => [c.start + firstColspan, c.end + firstColspan])
        .concat(drawColumnGroups ? [0, firstColspan] : []),
    ),
  );

  setContext(DataTableContextKey, {
    get tableState() {
      return tableState;
    },
    get cols() {
      return cols;
    },
    get flatCols() {
      return flatCols;
    },
    get rows() {
      return rows;
    },
    get borders() {
      return borders;
    },
    get hasAlerts() {
      return hasAlerts;
    },
    get showCheckboxes() {
      return showCheckboxes;
    },
    tooltip,
    rowSettings: {
      get nameFunction() {
        return nameFunction;
      },
      get hidePeak() {
        return hidePeak;
      },
      get countHint() {
        return countHint;
      },
      get countHintTooltip() {
        return countHintTooltip;
      },
      get showGroupHeaderAlerts() {
        return showGroupHeaderAlerts;
      },
      get rowNameBold() {
        return rowNameBold;
      },
      get onRowClick() {
        return onRowClick;
      },
    },
  });

  // Params
  $effect(() => {
    expanded;
    rows;
    untrack(reExpand);
  });

  let selectedAggregate = $derived.by(() => {
    const selectedRows = tableState.getSelectedRows();
    return selectedRows.length > 0
      ? aggregateRows(flatCols, selectedRows, 'grouping')
      : { name: '-', data: {}, tags: [], parameters: {}, type: <const>'leaf' };
  });

  let wrapperWidth = $state(320);

  onMount(() => {
    const wrapper = tableWrapper;
    if (!wrapper) return;

    const updateWidth = () => {
      wrapperWidth = Math.floor(wrapper.clientWidth - 4);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(wrapper);

    return () => {
      observer.disconnect();
    };
  });

  // #####
  // ## Virtualization
  // #####
  let rowElementRefs: (HTMLTableRowElement | undefined)[] = $state([]);

  const virtualizer = createVirtualizer({
    scrollElement: () => scrollParent as Element | undefined,
    count: () => tableState.rowCount,
    estimatedSize: 32,
    overscan: 30,
  });

  // Measure visible row elements for accurate virtualization
  $effect(() => {
    for (const item of virtualizer.virtualItems) {
      const el = rowElementRefs[item.index];
      virtualizer.measureElement(el, item.index);
    }
  });

  // #####
  // ## Column widths for DataTableLayout
  // #####
  // Body rows use CSS grid so each column needs an explicit width.
  // Treat configured widths as minimums and spread any extra wrapper width
  // across the flexible columns so the table fills the available space.
  let flatColWidths = $derived.by((): (number | string)[] => {
    const widths: (number | string)[] = [];
    const flexibleIndexes: number[] = [];

    if (showCheckboxes) widths.push(columnWidths?.checkbox ?? 40);
    if (hasAlerts) widths.push(columnWidths?.alert ?? 32);

    const nameIndex = widths.length;
    widths.push(columnWidths?.name ?? 250);
    if (typeof widths[nameIndex] === 'number') {
      flexibleIndexes.push(nameIndex);
    }

    for (const col of flatCols) {
      const index = widths.length;
      widths.push(col.width ?? columnWidths?.data ?? 120);
      if (typeof widths[index] === 'number') {
        flexibleIndexes.push(index);
      }
    }

    const baseWidth = widths.reduce<number>(
      (sum, width) => sum + (typeof width === 'number' ? width : 0),
      0,
    );
    const extraWidth = Math.max(0, wrapperWidth - baseWidth);

    if (extraWidth > 0 && flexibleIndexes.length > 0) {
      const flexibleWidths = flexibleIndexes.map((i) => widths[i] as number);
      const totalFlexible = flexibleWidths.reduce((s, w) => s + w, 0);
      let distributed = 0;

      for (let i = 0; i < flexibleIndexes.length; i++) {
        const index = flexibleIndexes[i];
        const share =
          i < flexibleIndexes.length - 1
            ? Math.round((flexibleWidths[i] / totalFlexible) * extraWidth)
            : extraWidth - distributed;
        widths[index] = (widths[index] as number) + share;
        distributed += share;
      }
    }

    return widths;
  });

  const colTemplate = $derived(
    flatColWidths.map((w) => (typeof w === 'number' ? `${w}px` : String(w))).join(' '),
  );
</script>

<div
  class="mb-4 flex h-fit w-full flex-col flex-wrap items-start justify-between gap-4 md:flex-row md:items-center"
>
  <FilterRow
    class="max-w-full overflow-visible"
    {rows}
    {useAnyTagFilter}
    bind:filterFn
    {tagLabel}
    {tagTitle}
  />
  {#if topRightChild !== undefined}
    {@render topRightChild()}
  {/if}
</div>
<DataTableLayout
  columnWidths={flatColWidths}
  totalSize={virtualizer.totalSize}
  classes={{
    mainWrapper: [`data-table`, wrapperClass],
    bottomWrapper: 'data-table-bottom',
  }}
  styles={{
    mainWrapper: `--grid-col-template: ${colTemplate};`,
  }}
  hideBottom={!showSelectedAggregate || tableState.getSelectedRows().length === 0}
  stickyHeader
  bind:wrapper={tableWrapper}
>
  <!-- Table Header -->
  {#snippet header()}
    <DataTableHeader onColumnClicked={(col) => columnClicked(col)} {quickSelect}>
      {#snippet checkbox()}
        <Checkbox
          selected={tableState.getIsAllSelected()}
          onSelect={(v) => tableState.selectAll(v)}
        />
      {/snippet}

      {#snippet name()}
        {@html nameColTitle}
        <DataTableSortingIcon sorting={tableState.getSortingState(-1)} col={{ type: 'name' }} />
      {/snippet}
    </DataTableHeader>
  {/snippet}
  {#each virtualizer.virtualItems as virtualItem (virtualItem.key)}
    {@const flatRow = tableState.flatRows[virtualItem.index]}
    {#if flatRow}
      {@const level = flatRow.depth}
      {@const row =
        unitConversion !== undefined && level in unitConversion
          ? convertTableRowUnits(flatRow.original, unitConversion[level])
          : flatRow.original}
      {@const isGroup = row.type === 'group' || row.type === 'lazyGroup'}
      {@const alert = mostSevereAlert(flatCols, row)}
      {@const isExpanded = tableState.expandedIds.has(flatRow.id)}
      {@const selected = tableState.getSelectionState(flatRow.id)}

      <DataTableRow
        {row}
        {selected}
        nameOnly={isGroup && (!showGroupAggregate || nameOnlyLayers > level)}
        groupHeader={isGroup}
        expanded={isExpanded}
        childrenLoading={loadingRowsIDs.has(flatRow.id)}
        onSelect={(v) => tableState.toggleSelect(flatRow.id, v)}
        onExpand={(v) => expandRow(flatRow, v)}
        {level}
        {alert}
        rowClass={[
          'min-h-8',
          onRowClick && 'cursor-pointer',
          virtualItem.index % 2 === 1 && 'is-odd-row',
        ]}
        rowHref={(r) => rowHref?.(r, flatRow.parentRows)}
        virtualIndex={virtualItem.index}
        bind:rowElementRef={rowElementRefs[virtualItem.index]}
        rowStyle={`transform: translateY(${virtualItem.start}px);`}
      />
    {/if}
  {/each}
  {#snippet bottomHeader()}
    <DataTableHeader>
      {#snippet name()}
        <span>Selected</span>
      {/snippet}
    </DataTableHeader>
  {/snippet}
  {#snippet bottomBody()}
    <DataTableRow row={selectedAggregate} selectable={false} />
  {/snippet}
</DataTableLayout>

{#if $tooltip && 'snippet' in $tooltip}
  <Tooltip>
    {@render $tooltip.snippet($tooltip.props[0], $tooltip.props[1])}
  </Tooltip>
{:else if $tooltip?.text.length}
  <Tooltip size={$tooltip?.width}>
    {@html $tooltip?.text ?? ''}
  </Tooltip>
{/if}
